import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import {
  LayoutDashboard, Users, UserPlus, Kanban, Activity as ActivityIcon,
  CalendarClock, HeartHandshake, BarChart3, Settings as Cog,
  LogOut, Search, Bell, Menu, X, Sprout, Languages,
} from "lucide-react";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { api } from "../lib/api";

const navItems = [
  { to: "/dashboard", key: "nav.dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/contacts",  key: "nav.contacts",  icon: Users,           tid: "nav-contacts" },
  { to: "/leads",     key: "nav.leads",     icon: UserPlus,        tid: "nav-leads" },
  { to: "/deals",     key: "nav.deals",     icon: Kanban,          tid: "nav-deals" },
  { to: "/activities",key: "nav.activities",icon: ActivityIcon,    tid: "nav-activities" },
  { to: "/followup",  key: "nav.followup",  icon: CalendarClock,   tid: "nav-followup" },
  { to: "/customers", key: "nav.customers", icon: HeartHandshake,  tid: "nav-customers" },
  { to: "/reports",   key: "nav.reports",   icon: BarChart3,       tid: "nav-reports" },
  { to: "/settings",  key: "nav.settings",  icon: Cog,             tid: "nav-settings" },
];

function SidebarInner({ onNavigate }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  return (
    <aside className="w-64 shrink-0 h-full flex flex-col justify-between" style={{ background: "#022C22", color: "#E2E8F0" }}>
      <div>
        <div className="flex items-center gap-2 px-5 py-5 border-b border-emerald-950/60">
          <div className="w-9 h-9 rounded-lg bg-emerald-600/20 flex items-center justify-center border border-emerald-500/30">
            <Sprout className="w-5 h-5 text-emerald-300" />
          </div>
          <div>
            <div className="text-white font-bold text-lg" style={{ fontFamily: "Outfit" }}>SatuCRM</div>
            <div className="text-[11px] text-emerald-300/70 -mt-0.5">Simple CRM · Bigger Ops</div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={onNavigate}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
              data-testid={n.tid}>
              <n.icon className="w-4 h-4" />
              <span>{t(n.key)}</span>
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="p-3 border-t border-emerald-950/60 space-y-2">
        <button onClick={() => setLang(lang === "id" ? "en" : "id")}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-100"
          data-testid="lang-toggle">
          <span className="flex items-center gap-2"><Languages className="w-4 h-4" /> Language</span>
          <Badge className="bg-emerald-500/20 text-emerald-100 border border-emerald-400/30 uppercase">{lang}</Badge>
        </button>
        <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-emerald-950/40">
          <Avatar className="w-8 h-8">
            <AvatarFallback className="bg-emerald-500/30 text-white text-xs">
              {(user?.full_name || "U").split(" ").map(x=>x[0]).slice(0,2).join("")}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white truncate">{user?.full_name}</div>
            <div className="text-[11px] text-emerald-300/70 truncate">{user?.role}</div>
          </div>
          <button onClick={logout} className="p-1.5 rounded hover:bg-emerald-900/60" title="Logout" data-testid="logout-btn">
            <LogOut className="w-4 h-4 text-emerald-200" />
          </button>
        </div>
      </div>
    </aside>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();

  const doSearch = async (val) => {
    setQ(val);
    if (!val || val.length < 2) { setResults(null); return; }
    try {
      const r = await api.get(`/search`, { params: { q: val } });
      setResults(r.data);
    } catch { setResults(null); }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:block h-screen sticky top-0"><SidebarInner /></div>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 px-4 lg:px-6 flex items-center gap-3 sticky top-0 z-30">
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 rounded-md hover:bg-slate-100" data-testid="mobile-menu">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              <SidebarInner />
            </SheetContent>
          </Sheet>

          <div className="flex-1 max-w-xl relative">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder={t("action.search")}
                value={q}
                onChange={(e) => doSearch(e.target.value)}
                className="pl-9 h-10 bg-slate-50 border-slate-200"
                data-testid="global-search-input"
              />
            </div>
            {results && (
              <div className="absolute top-11 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-50 max-h-96 overflow-y-auto" data-testid="global-search-results">
                {["contacts","leads","deals","customers"].map((k) => (
                  results[k]?.length > 0 && (
                    <div key={k} className="mb-3">
                      <div className="text-xs uppercase text-slate-500 mb-1 font-semibold">{k}</div>
                      {results[k].map((it) => (
                        <button key={it.id} onClick={() => { setResults(null); setQ(""); navigate(`/${k}`); }}
                          className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm">
                          {it.name || it.full_name} <span className="text-slate-400">· {it.company}</span>
                        </button>
                      ))}
                    </div>
                  )
                ))}
                {!Object.values(results).some((v) => v.length) && (
                  <div className="text-sm text-slate-500 py-2 text-center">Tidak ada hasil</div>
                )}
              </div>
            )}
          </div>

          <button className="p-2 rounded-md hover:bg-slate-100 relative" data-testid="notifications-btn">
            <Bell className="w-5 h-5 text-slate-600" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-emerald-500 rounded-full"></span>
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-1 rounded-md hover:bg-slate-100" data-testid="user-menu">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-emerald-800 text-white text-xs">
                    {(user?.full_name || "U").split(" ").map(x=>x[0]).slice(0,2).join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left leading-tight">
                  <div className="text-sm font-medium text-slate-800">{user?.full_name}</div>
                  <div className="text-[11px] text-slate-500">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.full_name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}>Pengaturan</DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout">Keluar</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
